import React, { FC, useCallback, useMemo, useState } from 'react';

import {
	BUTTON_TYPES,
	EvButton,
	EvIcon,
	EvSection,
	EvTextInput,
	EvTitle,
	PlusIcon,
	TOAST_STATUS
} from '@evinced-private/ui-common';

import { domainInputPlaceholder } from 'src/consts/domainPlaceholderText';
import { PropertyHelper } from 'src/helpers/PropertyHelper';
import { buildAnnouncement, getUrlsFromCSV, removeDuplications } from 'src/helpers/SeedsListHelper';
import { formatURL } from 'src/helpers/UrlNormalizingHelper';
import { validateUrlFormat } from 'src/helpers/UrlValidationHelper';
import { IUrlSeedDisplay } from 'src/interfaces/UrlSeedDisplay';
import { SETTINGS_SECTION_TYPES } from 'src/types/SettingsSectionTypes';

import { SeedsTable } from '../../../components/property-settings/sections/seeds-list/SeedsTable';
import { usePropertySettings } from '../context';
import { clearSectionError, setSeeds } from '../state';

import { SEED_LIST_DESCRIPTION_ID, SEED_LIST_TITLE_ID } from './SeedsSettingsV2.constants';
import { SEEDS_LABELS } from './SeedsSettingsV2.labels';
import { SeedsLocalState } from './SeedsSettingsV2.types';
import { SeedsTableActions } from './SeedsTableActions';

import '../../../components/property-settings/sections/seeds-list/SeedsList.scss';

const ADD_SEED_PLACEHOLDER = `Add URL (${domainInputPlaceholder})`;

const getToastStatus = (hasNew: boolean, hasErrors: boolean): TOAST_STATUS => {
	if (hasNew && !hasErrors) {
		return TOAST_STATUS.SUCCESS;
	}
	if (hasNew && hasErrors) {
		return TOAST_STATUS.INFO;
	}
	if (!hasNew && hasErrors) {
		return TOAST_STATUS.FAIL;
	}
	return TOAST_STATUS.INFO;
};

const INITIAL_LOCAL_STATE: SeedsLocalState = {
	seedEntry: '',
	seedError: '',
	selectedSeeds: [],
	isImportInProgress: false,
	toastStatus: TOAST_STATUS.SUCCESS,
	importAnnouncement: '',
	showImportToast: false
};

export const SeedsSettingsV2: FC = () => {
	const {
		state: { draft: property, errors },
		dispatch,
		isListMode
	} = usePropertySettings();

	const propertyError = useMemo(
		() => errors.find((e) => e.sectionId === SETTINGS_SECTION_TYPES.SEEDS),
		[errors]
	);

	const onSeedsChange = useCallback(
		(data: { seeds?: { url: string }[]; urls?: string[] }) => {
			dispatch(setSeeds(data.seeds, data.urls));
		},
		[dispatch]
	);

	const onClearError = useCallback(() => {
		dispatch(clearSectionError(SETTINGS_SECTION_TYPES.SEEDS));
	}, [dispatch]);

	const [localState, setLocalState] = useState<SeedsLocalState>(INITIAL_LOCAL_STATE);

	const {
		seedEntry,
		seedError,
		selectedSeeds,
		isImportInProgress,
		toastStatus,
		importAnnouncement,
		showImportToast
	} = localState;

	const title = isListMode ? SEEDS_LABELS.URLS_LIST_TITLE : SEEDS_LABELS.SEEDS_LIST_TITLE;
	const moreInfoText = isListMode ? SEEDS_LABELS.MORE_INFO_LIST : SEEDS_LABELS.MORE_INFO_CRAWL;

	let seeds: IUrlSeedDisplay[] = PropertyHelper.getPropertyDiscoverySeeds(property);
	if (isListMode) {
		seeds = PropertyHelper.getPropertyListModeSeeds(property);
	}

	const setUrlRegex = (value: string): void => {
		setLocalState((prev) => ({
			...prev,
			seedEntry: value,
			seedError: prev.seedError ? '' : prev.seedError
		}));
	};

	const getPropertySeeds = useCallback((): { url: string; id: number }[] => {
		if (isListMode && property.urls) {
			return property.urls.map((url, index) => ({ url, id: index }));
		}
		return [...PropertyHelper.getPropertyDiscoverySeeds(property)];
	}, [isListMode, property]);

	const emitSeeds = useCallback(
		(seedsList: { url: string }[]): void => {
			if (isListMode) {
				onSeedsChange({ urls: seedsList.map((s) => s.url) });
			} else {
				onSeedsChange({ seeds: seedsList.map((s) => ({ url: s.url })) });
			}
		},
		[isListMode, onSeedsChange]
	);

	const addSeedToProperty = (seed: string): void => {
		const newSeed = formatURL(seed);
		const isValidUrl = validateUrlFormat(newSeed);

		if (!isValidUrl.isValid) {
			setLocalState((prev) => ({ ...prev, seedError: isValidUrl.errorMessages[0] }));
			return;
		}

		const currentSeeds = getPropertySeeds();
		const seedAlreadyExists = currentSeeds.find((s) => formatURL(s.url) === newSeed);

		if (!seedAlreadyExists) {
			emitSeeds([...currentSeeds, { url: newSeed }]);
			setLocalState((prev) => ({ ...prev, seedEntry: '', seedError: '' }));
			onClearError();
		} else {
			setLocalState((prev) => ({
				...prev,
				seedError: isListMode ? SEEDS_LABELS.DUPLICATE_URL : SEEDS_LABELS.DUPLICATE_SEED
			}));
		}
	};

	const updatePropertySeeds = useCallback(
		(seedsList: { url: string }[], closeModal: () => void): void => {
			emitSeeds(seedsList);
			setLocalState((prev) => ({
				...prev,
				selectedSeeds: [],
				isImportInProgress: false
			}));
			closeModal();
		},
		[emitSeeds]
	);

	const onToastClose = useCallback((): void => {
		setLocalState((prev) => ({ ...prev, showImportToast: false }));
	}, []);

	const setSelectedSeeds = useCallback((selected: IUrlSeedDisplay[]): void => {
		setLocalState((prev) => ({ ...prev, selectedSeeds: selected }));
	}, []);

	const onImport = useCallback(
		(closeModal: () => void, browsedFileContent: []): void => {
			setLocalState((prev) => ({ ...prev, isImportInProgress: true }));
			closeModal();

			requestAnimationFrame(() => {
				const { validUrls } = getUrlsFromCSV([...browsedFileContent]);

				const browsedCount = browsedFileContent.length;
				const errCount = browsedCount - validUrls.length;

				const currentSeeds = getPropertySeeds();
				let mergedSeeds;
				let newCount = validUrls.length || 0;
				if (validUrls.length > 0) {
					mergedSeeds = removeDuplications([...currentSeeds, ...validUrls]);
					newCount = mergedSeeds.length - currentSeeds.length;
				} else {
					mergedSeeds = currentSeeds;
				}
				const dupCount = validUrls.length - newCount;

				const newToastStatus = getToastStatus(newCount > 0, errCount > 0);

				setLocalState((prev) => ({
					...prev,
					importAnnouncement: buildAnnouncement(newCount, errCount, dupCount),
					toastStatus: newToastStatus,
					showImportToast: true
				}));
				updatePropertySeeds(mergedSeeds, closeModal);
			});
		},
		[getPropertySeeds, updatePropertySeeds]
	);

	return (
		<EvSection
			className="seeds-list-section"
			ariaLabelledby={SEED_LIST_TITLE_ID}
			ariaDescribedby={SEED_LIST_DESCRIPTION_ID}
		>
			<div className="section-header">
				<div className="title">
					<EvTitle
						id={SEED_LIST_TITLE_ID}
						descriptionId={SEED_LIST_DESCRIPTION_ID}
						titleText={title}
						MoreInfoContent={<>{moreInfoText}</>}
					/>
				</div>
				<div className="add-seed">
					<div className="seed-input">
						<EvTextInput
							ariaLabel={ADD_SEED_PLACEHOLDER}
							error={seedError}
							placeholder={ADD_SEED_PLACEHOLDER}
							value={seedEntry}
							onChange={setUrlRegex}
							onEnter={() => {
								addSeedToProperty(seedEntry);
							}}
						/>
						{propertyError ? <div className="seed-error">{propertyError.message}</div> : null}
					</div>
					<EvButton
						type={BUTTON_TYPES.ICON}
						ariaLabel={SEEDS_LABELS.ADD_SEED_TITLE}
						onClick={() => {
							addSeedToProperty(seedEntry);
						}}
					>
						<EvIcon icon={PlusIcon} />
					</EvButton>
					<SeedsTableActions
						seeds={seeds}
						selectedSeeds={selectedSeeds}
						isListMode={isListMode}
						isImportInProgress={isImportInProgress}
						showImportToast={showImportToast}
						importAnnouncement={importAnnouncement}
						toastStatus={toastStatus}
						onToastClose={onToastClose}
						onImport={onImport}
						updatePropertySeeds={updatePropertySeeds}
					/>
				</div>
			</div>
			<EvSection className="section-body" ariaLabel={SEEDS_LABELS.SECTION_ARIA_LABEL}>
				<SeedsTable
					seeds={seeds}
					selectedSeeds={selectedSeeds}
					updatePropertySeeds={updatePropertySeeds}
					setSelectedSeeds={setSelectedSeeds}
					propertyId={property.id}
					isListMode={isListMode}
					isImporting={isImportInProgress}
				/>
			</EvSection>
		</EvSection>
	);
};
